/**
 * MonthlyBudgetSetup - Full-page budget setup with Income/Budgets tabs
 * Extracted from BudgetTab.jsx for modularity
 */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Check, Copy, ChevronLeft, ChevronRight, Settings, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { formatCurrency, getCurrentCurrency } from "../../utils/formatters";
import customToast from "../../utils/customToast";
import { usePlan } from "../../utils/plan"; // plan_08

// Floating budget edit popup - always visible above keyboard
export const BudgetEditPopup = ({ label, hint, initialValue, accent, currency, onSave, onCancel, allowNegative, isWarm, isPink, theme }) => {
  const [value, setValue] = useState(initialValue || '');
  const plan = usePlan();
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full bg-white rounded-t-3xl px-6 pt-5 shadow-2xl wy-plan-sheet"
        style={{ paddingBottom: "max(2.5rem, env(safe-area-inset-bottom, 0px) + 320px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center mb-4">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>
        <p className="text-base font-semibold text-gray-700 mb-1">{label}</p>
        {hint && <p className="text-xs text-gray-400 mb-3">{hint}</p>}
        <div className="relative mb-4">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-400">{currency.symbol}</span>
          <input
            type={allowNegative ? "text" : "number"}
            inputMode={allowNegative ? "text" : "decimal"}
            value={value}
            onChange={(e) => {
              if (allowNegative) {
                const v = e.target.value;
                if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v)) setValue(v);
              } else {
                setValue(e.target.value);
              }
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); onSave(value); } }}
            placeholder="0"
            autoFocus
            className="w-full h-12 pl-10 pr-4 text-lg font-bold rounded-2xl border-2 border-gray-200 focus:outline-none"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 h-12 rounded-full border-2 border-gray-200 text-gray-600 font-semibold text-sm active:scale-[0.98] transition-transform"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(value)}
            className={`flex-1 h-12 rounded-full text-white font-semibold text-sm shadow-md active:scale-[0.98] transition-transform`}
            style={{ background: plan ? '#1F1E24' : isPink ? '#BA5580' : isWarm ? '#607F5E' : '#5877A6' }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  , document.body);
};


// Copy-from-last-month sheet. Same portal + bottom-sheet shape as BudgetEditPopup
// above, so it sits on the keyboard-safe pattern already used in this file.
const CopyLastMonthSheet = ({
  onBack,
  groups,
  prevMonthName,
  monthName,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  copyNotes,
  setCopyNotes,
  newNotesCount,
  onConfirm,
  onCancel,
  isPink,
  isWarm,
  theme,
}) => {
  const allItems = groups.flatMap((g) => g.items);
  const copyable = allItems.filter((i) => !i.exists);
  const skippedCount = allItems.length - copyable.length;
  const selectedCount = copyable.filter((i) => selected[i.key]).length;

  // plan_08: Money plan - lilac link, black ticks, soft box, green switch.
  const plan = usePlan();
  const labelColor = plan ? '#7A5DD3' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const checkColor = plan ? '#1F1E24' : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE';
  const notesTintBg = plan ? '#F7F5FB' : isPink ? '#F9EEF6' : isWarm ? '#F3EDE0' : '#EEF1FF';
  const serifFont = { fontFamily: "'DM Serif Display', serif" };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full bg-white rounded-t-3xl shadow-2xl flex flex-col"
        style={{ height: '88vh', paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="copy-last-month-sheet"
        data-plan-sheet="1"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="px-6 pt-2 flex-shrink-0">
          <div className="flex items-start gap-2.5">
            {onBack && (
              <button
                onClick={onBack}
                className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5"
                aria-label="Back"
                data-testid="copy-last-month-back"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[22px] leading-tight text-gray-800" style={serifFont}>
                Copy from {prevMonthName}
              </p>
              <p className="text-[12.5px] text-gray-400 mt-0.5">
                {copyable.length} item{copyable.length === 1 ? '' : 's'} to copy
                {skippedCount > 0 ? ` · ${skippedCount} already in ${monthName}` : ''}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0"
              aria-label="Close"
              data-testid="copy-last-month-close"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {skippedCount > 0 && (
            <p className="text-[11.5px] leading-relaxed text-gray-500 bg-gray-50 rounded-xl px-3 py-2.5 mt-3">
              Only the currently empty items get copied. Amounts you have already set in {monthName} stay as they are.
            </p>
          )}

          <div className="flex items-center justify-between py-3 mt-1 border-b border-gray-100">
            <span className="text-[13px] font-semibold text-gray-600">
              {selectedCount} of {copyable.length} selected
            </span>
            <button
              onClick={selectedCount === copyable.length ? onClearAll : onSelectAll}
              className="text-[13px] font-bold"
              style={{ color: labelColor }}
              data-testid="copy-last-month-toggle-all"
            >
              {selectedCount === copyable.length ? 'Clear all' : 'Select all'}
            </button>
          </div>
        </div>

        {/* Grouped list */}
        <div className="px-6 pt-3 overflow-y-auto flex-1 min-h-0">
          {groups.map((group) => (
            <div key={group.title} className="mb-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400 mb-1.5">
                {group.title}
                {group.items.length > 1 ? ` · ${group.items.length}` : ''}
              </p>
              <div className="border border-gray-100 rounded-2xl overflow-hidden">
                {group.items.map((item) =>
                  item.exists ? (
                    <div
                      key={item.key}
                      className="flex items-center gap-3 px-3.5 py-3 border-b border-gray-50 last:border-b-0"
                      style={{ background: '#FCFCFD' }}
                      data-testid={`copy-item-skipped-${item.key}`}
                    >
                      <Check className="w-5 h-5 text-gray-300 flex-shrink-0" />
                      <span className="flex-1 text-[14px] text-gray-400 truncate" style={{ fontWeight: 500 }}>
                        {item.label}
                      </span>
                      <span className="text-[10.5px] font-bold text-gray-500 bg-gray-100 rounded-full px-2.5 py-1 flex-shrink-0">
                        Already added
                      </span>
                    </div>
                  ) : (
                    <label
                      key={item.key}
                      className="flex items-center gap-3 px-3.5 py-3 border-b border-gray-50 last:border-b-0 cursor-pointer"
                      data-testid={`copy-item-${item.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[item.key]}
                        onChange={() => onToggle(item.key)}
                        className="w-5 h-5 flex-shrink-0"
                        style={{ accentColor: checkColor }}
                      />
                      <span className="flex-1 text-[14px] text-gray-800 truncate" style={{ fontWeight: 500 }}>
                        {item.label}
                      </span>
                      <span className="text-[15px] text-gray-800 flex-shrink-0" style={serifFont}>
                        {formatCurrency(item.amount)}
                      </span>
                    </label>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Notes switch + confirm */}
        <div className="px-6 pt-3 flex-shrink-0 border-t border-gray-100">
          {newNotesCount > 0 && (
            <button
              onClick={() => setCopyNotes((v) => !v)}
              role="switch"
              aria-checked={copyNotes}
              className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 mb-3"
              style={{ background: notesTintBg }}
              data-testid="copy-last-month-notes-switch"
            >
              <span className="text-[16px]">📝</span>
              <span className="flex-1 text-left min-w-0">
                <span className="block text-[13.5px] font-bold text-gray-800">Also copy memos</span>
                <span className="block text-[11.5px] text-gray-500">
                  {newNotesCount} from {prevMonthName}
                </span>
              </span>
              <span
                className="w-11 h-6 rounded-full flex items-center px-0.5 flex-shrink-0 transition-colors"
                style={{
                  background: copyNotes ? (plan ? '#9ED9B6' : checkColor) : '#D9D4DC',
                  justifyContent: copyNotes ? 'flex-end' : 'flex-start',
                }}
              >
                <span className="w-5 h-5 rounded-full bg-white shadow-sm block" />
              </span>
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={selectedCount === 0 && !copyNotes}
            className={`w-full h-12 rounded-full text-white font-semibold text-sm shadow-md active:scale-[0.98] transition-transform disabled:opacity-40`}
            // Same button colour as the Contributions Planner.
            style={{ background: plan ? '#1F1E24' : isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE' }}
            data-testid="copy-last-month-confirm"
          >
            {selectedCount > 0
              ? `Copy ${selectedCount} item${selectedCount === 1 ? '' : 's'} to ${monthName}`
              : `Copy memos to ${monthName}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const MonthlyBudgetSetup = ({
  theme,
  onClose,
  viewMonthKey,
  viewMonth,
  budgetSettings,
  saveUserData,
  cards,
  colorMap,
  categoriesWithActivity,
  categoryBudgets,
  availableSinkingFunds,
  availableInvestmentAccounts,
  userDebts,
  onResetMonth,
  // `only` renders a single slice of this component, for embedding in a sheet:
  //   "income"   - amount to budget + the per-card carry-in (the Money sheet)
  //   "amount"   - just amount to budget
  //   "balances" - just the per-card carry-in, labelled Starting Balances
  //   "copy"     - renders nothing and opens the copy-from-last-month sheet
  // In every case: no tab pill, no reset bin, no floating Done.
  only = null,
  // Optional: when the sheet was reached from the month-setup menu, this is
  // the way back to it. Absent means the sheet only offers a way out.
  onBack = null,
  // { cardName: closing balance last month }. A suggestion for this month's
  // starting balances - never written until the user taps to accept it.
  prevCardBalances = {},
}) => {
  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';
  const plan = usePlan(); // plan_08

  const warmColors = {
    savings: { text: 'text-[#5D6B4D]', border: 'border-[#D4DBC9]' },
    investments: { text: 'text-[#4A6B6B]', border: 'border-[#C9D9D9]' },
    debt: { text: 'text-[#8B6B4D]', border: 'border-[#E0D4C4]' },
  };

  // Internal state
  const [activeTab, setActiveTab] = useState(only === 'copy' ? 'none' : (only === 'notes' ? 'notes' : 'income'));
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [budgetViewType, setBudgetViewType] = useState('spending');
  const [leftoverAllocations, setLeftoverAllocations] = useState({});
  const [editingBudgetAmount, setEditingBudgetAmount] = useState(false);
  const [localBudgetAmount, setLocalBudgetAmount] = useState('');
  const [editingCategoryBudget, setEditingCategoryBudget] = useState(null);
  const [categoryBudgetValue, setCategoryBudgetValue] = useState('');
  const [editingSavingsBudget, setEditingSavingsBudget] = useState(null);
  const [savingsBudgetValue, setSavingsBudgetValue] = useState('');
  const [editingDebtBudget, setEditingDebtBudget] = useState(null);
  const [debtBudgetValue, setDebtBudgetValue] = useState('');
  const [monthNotes, setMonthNotes] = useState(budgetSettings.notes_by_month?.[viewMonthKey] || '');
  const [editingReminderIndex, setEditingReminderIndex] = useState(null);
  const [reminderDraft, setReminderDraft] = useState('');
  const [addingReminder, setAddingReminder] = useState(false);
  const [newReminderDraft, setNewReminderDraft] = useState('');
  const scrollRef = useRef(null);
  const [showCopySheet, setShowCopySheet] = useState(false);
  const [copySelected, setCopySelected] = useState({});
  const [copyNotes, setCopyNotes] = useState(true);

  // Notes tab is otherwise all local state (for inline editing), so unlike
  // Income/Budgets — which read straight from budgetSettings every render —
  // it needs to be explicitly resynced whenever the viewed month changes.
  useEffect(() => {
    setMonthNotes(budgetSettings.notes_by_month?.[viewMonthKey] || '');
    setEditingReminderIndex(null);
    setReminderDraft('');
    setAddingReminder(false);
    setNewReminderDraft('');
    setShowCopySheet(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonthKey]);

  // Mini popup for keyboard-safe budget editing
  const [budgetPopup, setBudgetPopup] = useState(null);
  // budgetPopup = { label, value, onSave, accent }


  // Computed from budgetSettings
  const monthlyBudget = budgetSettings.budget_by_month?.[viewMonthKey] || 0;
  const monthlyCategoryBudgets = categoryBudgets[viewMonthKey] || {};
  const monthlySavingsBudgets = (budgetSettings.savings_budgets_by_month || {})[viewMonthKey] || {};
  const monthlyDebtBudgets = (budgetSettings.debt_budgets_by_month || {})[viewMonthKey] || {};

  const totalCategoryBudgets = Object.entries(monthlyCategoryBudgets)
    .filter(([cat]) => categoriesWithActivity.some(c => c.name === cat))
    .reduce((sum, [, b]) => sum + (b || 0), 0);
  const validSavingsIds = new Set([
    ...(availableSinkingFunds || []).map(f => f.id),
    ...(availableInvestmentAccounts || []).map(a => a.id)
  ]);
  const validDebtIds = new Set((userDebts || []).map(d => d.id));

  const savingsDeposits = Object.entries(monthlySavingsBudgets)
    .filter(([id]) => validSavingsIds.has(id))
    .reduce((sum, [, b]) => sum + Math.max(b || 0, 0), 0);
  const savingsWithdrawals = Object.entries(monthlySavingsBudgets)
    .filter(([id]) => validSavingsIds.has(id))
    .reduce((sum, [, b]) => sum + Math.abs(Math.min(b || 0, 0)), 0);
  const totalDebtBudgetsVal = Object.entries(monthlyDebtBudgets)
    .filter(([id]) => validDebtIds.has(id))
    .reduce((sum, [, b]) => sum + (b || 0), 0);
  const totalAllocated = totalCategoryBudgets + savingsDeposits + totalDebtBudgetsVal;

  // Leftover computation
  const savedAllocations = budgetSettings.leftover_allocations_by_month?.[viewMonthKey] || {};
  const mergedAllocations = { ...savedAllocations };
  Object.keys(leftoverAllocations).forEach(cardName => {
    mergedAllocations[cardName] = parseFloat(leftoverAllocations[cardName]) || 0;
  });
  const totalLeftover = Object.values(mergedAllocations).reduce((a, b) => a + b, 0);

  // Carry-in suggestion. Always offered, whether or not last month has ended
  // and whether or not these rows already hold figures - tapping it takes the
  // numbers as they stand today, which is what you want when you set a month
  // up early and again once the old month has actually closed.
  const carryFlagsForMonth = budgetSettings.leftover_carry_by_month?.[viewMonthKey] || {};
  const carryCards = cards.filter(c => c.type !== 'credit' && typeof prevCardBalances[c.name] === 'number');
  const carrySuggestionTotal = carryCards.reduce((sum, c) => sum + prevCardBalances[c.name], 0);
  const carryCardCount = carryCards.length;
  // Offer it unless every card is already following last month. Once they are,
  // the figures keep themselves current and there is nothing left to accept.
  const hasCarrySuggestion = carryCards.some(c => !carryFlagsForMonth[c.name]);

  const applyCarrySuggestion = () => {
    const month = { ...(budgetSettings.leftover_allocations_by_month?.[viewMonthKey] || {}) };
    const flags = { ...(budgetSettings.leftover_carry_by_month?.[viewMonthKey] || {}) };
    carryCards.forEach(c => {
      month[c.name] = Math.round(prevCardBalances[c.name] * 100) / 100;
      flags[c.name] = true;
    });
    const total = Object.values(month).reduce((a, b) => a + b, 0);
    saveUserData({
      budget_settings: {
        ...budgetSettings,
        leftover_allocations_by_month: {
          ...(budgetSettings.leftover_allocations_by_month || {}),
          [viewMonthKey]: month,
        },
        leftover_by_month: { ...(budgetSettings.leftover_by_month || {}), [viewMonthKey]: total },
        // From here these cards follow last month until a row is typed over.
        leftover_carry_by_month: {
          ...(budgetSettings.leftover_carry_by_month || {}),
          [viewMonthKey]: flags,
        },
      },
    });
    setLeftoverAllocations({});
  };

  const available = monthlyBudget + totalLeftover;
  const budgetBase = monthlyBudget + savingsWithdrawals;
  const allocatedPercent = budgetBase > 0 ? Math.min(100, Math.round((totalAllocated / budgetBase) * 100)) : 0;
  const remaining = budgetBase - totalAllocated;

  const currency = getCurrentCurrency();

  // Month name from viewMonthKey
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = monthNames[parseInt(viewMonthKey.split('-')[1]) - 1];

  // Theme styles
  const pageBg = isPink
    ? '#F6ECF5'
    : isWarm
      ? '#FAF7F2'
      : '#EEF2FF';
  const pageBgFade = isPink ? '#F6ECF5' : isWarm ? '#FAF7F2' : '#EEF2FF';
  const toggleBg = 'bg-white/75';
  const activeTabText = isPink ? 'text-[#1A1A1F]' : isWarm ? 'text-[#2D5A3D]' : 'text-[#3730a3]';
  const accentText = plan ? 'text-[#7A5DD3]' : isPink ? 'text-[#e91e8c]' : isWarm ? 'text-[#2D5A3D]' : 'text-[#3730a3]';
  const accentBoldText = plan ? 'text-[#7A5DD3]' : isPink ? 'text-pink-600' : isWarm ? 'text-[#6B5B3D]' : 'text-indigo-600';
  const iconBg = plan ? 'bg-[#F5F2FA]' : isPink ? 'bg-pink-200' : isWarm ? 'bg-[#D4C4A8]' : 'bg-indigo-200';
  const iconColor = plan ? 'text-[#6C6879]' : isPink ? 'text-pink-600' : isWarm ? 'text-[#6B5B3D]' : 'text-indigo-600';
  const sectionLabel = plan ? 'text-[#26252E]' : isPink ? 'text-[#7B5EA7]' : isWarm ? 'text-[#2D5A3D]' : 'text-[#4361EE]';
  const summaryCardBg = 'bg-white';
  const progressBg = plan ? 'bg-[#B9A6F0]' : isPink ? 'bg-pink-500' : isWarm ? 'bg-[#5D6B4D]' : 'bg-indigo-500';
  const progressTrack = plan ? 'bg-[#F0EDF6]' : isPink ? 'bg-pink-100' : isWarm ? 'bg-[#E8EDE4]' : 'bg-indigo-100';
  const saveGradientStyle = isPink
    ? { background: '#7B5EA7' }
    : isWarm
      ? { background: '#2D5A3D' }
      : { background: '#4361EE' };

  // Restyle pass (Sept 2026): shared shadow + serif-number style, plus
  // theme-aware colours for the tiles/chips added below
  const sectionLabelColor = plan ? '#7A5DD3' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const cardShadow = plan ? { boxShadow: 'none' } : { boxShadow: '0 10px 26px -14px rgba(120,60,110,.22)' };
  const serifFont = { fontFamily: "'DM Serif Display', serif" };
  const leftoverTileBg = plan ? '#FAE4ED' : isPink ? '#F3CCD6' : isWarm ? '#EADFC9' : '#DBE7F5';
  const leftoverTileText = plan ? '#C04B7C' : isPink ? '#C23A72' : isWarm ? '#8B6B4D' : '#2C5478';
  const availableTileBg = plan ? '#ECE6FA' : isPink ? '#E9DEF2' : isWarm ? '#DCE5D0' : '#E0E7FF';
  const availableTileText = plan ? '#7A5DD3' : isPink ? '#7B5EA7' : isWarm ? '#4A6B54' : '#4338CA';
  const leftoverInputBg = plan ? '#F7F5FB' : isPink ? '#F5E9F1' : isWarm ? '#F3EDE0' : '#EAEFFA';
  const reminderChipBg = plan ? 'transparent' : // plan_09: a lavender dot, no square
    isPink ? '#F3CCD6' : isWarm ? '#EADFC9' : '#DBE7F5';
  const addReminderChipBg = plan ? '#F5F2FA' : isPink ? '#F3ECF7' : isWarm ? '#F0EAE0' : '#EEF1FF';

  // Functions
  const updateBudgetAmount = (value) => {
    const parsed = parseFloat(value) || 0;
    setLocalBudgetAmount(parsed > 0 ? parsed.toString() : '');
    const newBudgetByMonth = { ...budgetSettings.budget_by_month, [viewMonthKey]: parsed };
    saveUserData({ budget_settings: { ...budgetSettings, budget_by_month: newBudgetByMonth } });
  };

  const handleLeftoverBlur = (cardName, rawValue) => {
    const value = parseFloat(rawValue) || 0;
    const newAllocations = {
      ...(budgetSettings.leftover_allocations_by_month || {}),
      [viewMonthKey]: {
        ...(budgetSettings.leftover_allocations_by_month?.[viewMonthKey] || {}),
        [cardName]: value
      }
    };
    const newTotalLeftover = Object.values(newAllocations[viewMonthKey] || {}).reduce((a, b) => a + b, 0);
    // Typing a figure means this card no longer follows last month. Only this
    // card - the others keep carrying.
    const newCarryFlags = {
      ...(budgetSettings.leftover_carry_by_month || {}),
      [viewMonthKey]: { ...(budgetSettings.leftover_carry_by_month?.[viewMonthKey] || {}), [cardName]: false },
    };
    saveUserData({
      budget_settings: {
        ...budgetSettings,
        leftover_allocations_by_month: newAllocations,
        leftover_carry_by_month: newCarryFlags,
        leftover_by_month: {
          ...(budgetSettings.leftover_by_month || {}),
          [viewMonthKey]: newTotalLeftover
        }
      }
    });
    setLeftoverAllocations(prev => {
      const next = { ...prev };
      delete next[cardName];
      return next;
    });
  };

  const updateCategoryBudget = (category) => {
    const value = parseFloat(categoryBudgetValue) || 0;
    const monthBudgets = { ...monthlyCategoryBudgets, [category]: value };
    saveUserData({ category_budgets_by_month: { ...categoryBudgets, [viewMonthKey]: monthBudgets } });
    setEditingCategoryBudget(null);
    setCategoryBudgetValue('');
  };

  const saveSavingsBudget = (itemId, value) => {
    const newMonthBudgets = { ...monthlySavingsBudgets, [itemId]: value };
    const allMonths = { ...(budgetSettings.savings_budgets_by_month || {}), [viewMonthKey]: newMonthBudgets };
    saveUserData({ budget_settings: { ...budgetSettings, savings_budgets_by_month: allMonths } });
  };

  const saveDebtBudget = (itemId, value) => {
    const newMonthBudgets = { ...monthlyDebtBudgets, [itemId]: value };
    const allMonths = { ...(budgetSettings.debt_budgets_by_month || {}), [viewMonthKey]: newMonthBudgets };
    saveUserData({ budget_settings: { ...budgetSettings, debt_budgets_by_month: allMonths } });
  };

  const saveMonthNotes = (text) => {
    saveUserData({
      budget_settings: {
        ...budgetSettings,
        notes_by_month: { ...(budgetSettings.notes_by_month || {}), [viewMonthKey]: text }
      }
    });
  };

  // Reminders (Notes tab) — monthNotes stays a single newline-joined string in
  // storage (no schema change), we just render each line as its own row.
  const reminderIcon = '📌';
  const reminderLines = monthNotes.split('\n').map(l => l.trim()).filter(Boolean);
  const [prevMonthYearNum, prevMonthNumRaw] = viewMonthKey.split('-').map(Number);
  const prevMonthKey = prevMonthNumRaw === 1
    ? `${prevMonthYearNum - 1}-12`
    : `${prevMonthYearNum}-${String(prevMonthNumRaw - 1).padStart(2, '0')}`;
  const prevMonthNotes = budgetSettings.notes_by_month?.[prevMonthKey] || '';

  const commitReminders = (list) => {
    const text = list.join('\n');
    setMonthNotes(text);
    saveMonthNotes(text);
  };
  const startEditReminder = (i) => {
    setEditingReminderIndex(i);
    setReminderDraft(reminderLines[i]);
  };
  const saveEditReminder = (i) => {
    const next = [...reminderLines];
    const trimmed = reminderDraft.trim();
    if (trimmed) { next[i] = trimmed; } else { next.splice(i, 1); }
    commitReminders(next);
    setEditingReminderIndex(null);
    setReminderDraft('');
  };
  const deleteReminder = (i) => {
    commitReminders(reminderLines.filter((_, idx) => idx !== i));
    setEditingReminderIndex(null);
  };
  const saveNewReminder = () => {
    const trimmed = newReminderDraft.trim();
    if (trimmed) { commitReminders([...reminderLines, trimmed]); }
    setNewReminderDraft('');
    setAddingReminder(false);
  };
  const copyPreviousMonthNotes = () => {
    setMonthNotes(prevMonthNotes);
    saveMonthNotes(prevMonthNotes);
  };

  // ── Copy from last month ───────────────────────────────────────────────
  // Every month already lives in budgetSettings keyed by "YYYY-MM", so copying
  // is read [prevMonthKey] -> write [viewMonthKey]. No backend change.
  const prevMonthName = monthNames[parseInt(prevMonthKey.split('-')[1], 10) - 1];
  const prevBudgetAmount = budgetSettings.budget_by_month?.[prevMonthKey] || 0;
  const prevCategoryBudgets = categoryBudgets[prevMonthKey] || {};
  const prevSavingsBudgets = (budgetSettings.savings_budgets_by_month || {})[prevMonthKey] || {};
  const prevDebtBudgets = (budgetSettings.debt_budgets_by_month || {})[prevMonthKey] || {};

  // Notes are a newline-joined string; only lines this month doesn't already have.
  const prevNoteLines = prevMonthNotes.split('\n').map(l => l.trim()).filter(Boolean);
  const newNoteLines = prevNoteLines.filter(l => !reminderLines.includes(l));

  const copyGroups = (() => {
    const groups = [];

    // Income is deliberately NOT here. Copying it belongs in the income
    // screen, next to the figure it would overwrite - offering it inside
    // "Copy budgets" means offering to change a number you may have just
    // typed two taps earlier, in a list about categories.

    const spending = categoriesWithActivity
      .filter(c => (prevCategoryBudgets[c.name] || 0) > 0)
      .map(c => ({
        key: `cat:${c.name}`,
        kind: 'category',
        id: c.name,
        label: c.name,
        amount: prevCategoryBudgets[c.name],
        exists: (monthlyCategoryBudgets[c.name] || 0) > 0,
      }));
    if (spending.length) groups.push({ title: 'Spending', items: spending });

    const savings = [...(availableSinkingFunds || []), ...(availableInvestmentAccounts || [])]
      .filter(f => (prevSavingsBudgets[f.id] || 0) !== 0)
      .map(f => ({
        key: `sav:${f.id}`,
        kind: 'savings',
        id: f.id,
        label: f.name,
        amount: prevSavingsBudgets[f.id],
        exists: (monthlySavingsBudgets[f.id] || 0) !== 0,
      }));
    if (savings.length) groups.push({ title: 'Savings & investing', items: savings });

    const debts = (userDebts || [])
      .filter(d => (prevDebtBudgets[d.id] || 0) > 0)
      .map(d => ({
        key: `debt:${d.id}`,
        kind: 'debt',
        id: d.id,
        label: d.name,
        amount: prevDebtBudgets[d.id],
        exists: (monthlyDebtBudgets[d.id] || 0) > 0,
      }));
    if (debts.length) groups.push({ title: 'Debts', items: debts });

    return groups;
  })();

  const copyableItems = copyGroups.flatMap(g => g.items).filter(i => !i.exists);
  const canCopyFromLastMonth = copyableItems.length > 0 || newNoteLines.length > 0;
  const copyableTotal = copyableItems
    .filter(i => i.kind !== 'income')
    .reduce((sum, i) => sum + Math.max(i.amount || 0, 0), 0);

  const openCopySheet = () => {
    const preselected = {};
    copyableItems.forEach(i => { preselected[i.key] = true; });
    setCopySelected(preselected);
    setCopyNotes(newNoteLines.length > 0);
    setShowCopySheet(true);
  };

  // only="copy" is a headless host: it mounts, opens the copy sheet, and the
  // sheet's own close paths unmount it again via onClose. That keeps ONE
  // implementation of copyGroups/runCopyFromLastMonth, here, where the month
  // data already lives - rather than a second copy of it in BudgetTab.
  useEffect(() => {
    if (only === 'copy') openCopySheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only]);

  const runCopyFromLastMonth = () => {
    const chosen = copyableItems.filter(i => copySelected[i.key]);
    const nextSettings = { ...budgetSettings };
    let nextCategoryBudgets = null;

    const incomeItem = chosen.find(i => i.kind === 'income');
    if (incomeItem) {
      nextSettings.budget_by_month = {
        ...(budgetSettings.budget_by_month || {}),
        [viewMonthKey]: incomeItem.amount,
      };
      setLocalBudgetAmount(incomeItem.amount > 0 ? incomeItem.amount.toString() : '');
    }

    const catItems = chosen.filter(i => i.kind === 'category');
    if (catItems.length) {
      const merged = { ...monthlyCategoryBudgets };
      catItems.forEach(i => { merged[i.id] = i.amount; });
      nextCategoryBudgets = { ...categoryBudgets, [viewMonthKey]: merged };
    }

    const savItems = chosen.filter(i => i.kind === 'savings');
    if (savItems.length) {
      const merged = { ...monthlySavingsBudgets };
      savItems.forEach(i => { merged[i.id] = i.amount; });
      nextSettings.savings_budgets_by_month = {
        ...(budgetSettings.savings_budgets_by_month || {}),
        [viewMonthKey]: merged,
      };
    }

    const debtItems = chosen.filter(i => i.kind === 'debt');
    if (debtItems.length) {
      const merged = { ...monthlyDebtBudgets };
      debtItems.forEach(i => { merged[i.id] = i.amount; });
      nextSettings.debt_budgets_by_month = {
        ...(budgetSettings.debt_budgets_by_month || {}),
        [viewMonthKey]: merged,
      };
    }

    if (copyNotes && newNoteLines.length > 0) {
      const combined = [...reminderLines, ...newNoteLines].join('\n');
      nextSettings.notes_by_month = {
        ...(budgetSettings.notes_by_month || {}),
        [viewMonthKey]: combined,
      };
      setMonthNotes(combined);
    }

    const payload = { budget_settings: nextSettings };
    if (nextCategoryBudgets) payload.category_budgets_by_month = nextCategoryBudgets;
    saveUserData(payload);

    setShowCopySheet(false);
    if (only === 'copy') onClose?.();

    const n = chosen.length;
    const msg = n > 0
      ? `Copied ${n} item${n === 1 ? '' : 's'} from ${prevMonthName}`
      : `Copied notes from ${prevMonthName}`;
    try { customToast?.success?.(msg); } catch (e) { /* toast is optional */ }
  };

  const formattedBudget = localBudgetAmount ? Math.round(parseFloat(localBudgetAmount) || 0).toLocaleString() : (monthlyBudget > 0 ? Math.round(monthlyBudget).toLocaleString() : '0');

  return (
    <div
      className="relative flex flex-col"
      data-testid="monthly-budget-setup"
    >
      {/* Header — Tab Toggle and Reset share one row, no empty space above it */}
      {!only && (
      <div className="pb-5 relative">
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-full p-1 flex bg-white/75 gap-1">
            <button
              onClick={() => setActiveTab('income')}
              className={`flex-1 py-2.5 rounded-full text-[14px] font-bold flex items-center justify-center gap-1.5 transition-all whitespace-nowrap ${
                activeTab === 'income' ? `bg-white shadow-sm ${activeTabText}` : 'text-gray-500'
              }`}
              data-testid="budget-setup-income-tab"
            >
              💰 Income
            </button>
            <button
              onClick={() => setActiveTab('budgets')}
              className={`flex-1 py-2.5 rounded-full text-[14px] font-bold flex items-center justify-center gap-1.5 transition-all whitespace-nowrap ${
                activeTab === 'budgets' ? `bg-white shadow-sm ${activeTabText}` : 'text-gray-500'
              }`}
              data-testid="budget-setup-budgets-tab"
            >
              📊 Budgets
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`flex-1 py-2.5 rounded-full text-[14px] font-bold flex items-center justify-center gap-1.5 transition-all whitespace-nowrap ${
                activeTab === 'notes' ? `bg-white shadow-sm ${activeTabText}` : 'text-gray-500'
              }`}
              data-testid="budget-setup-notes-tab"
            >
              📝 Notes
            </button>
          </div>
          {canCopyFromLastMonth && (
            <button
              onClick={openCopySheet}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/75 hover:bg-white transition-colors flex-shrink-0"
              data-testid="budget-setup-copy-btn"
              aria-label={`Copy from ${prevMonthName}`}
            >
              <Copy className="w-4 h-4" style={{ color: plan ? '#6C6879' : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE' }} />
            </button>
          )}
          {onResetMonth && (
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/75 hover:bg-white transition-colors flex-shrink-0"
              data-testid="budget-setup-reset-btn"
              aria-label="Reset month"
            >
              <Trash2 className="w-4 h-4" style={{ color: '#8A8194' }} />
            </button>
          )}
        </div>
      </div>
      )}

      {/* Content — flows with the page now instead of scrolling independently */}
      <div ref={scrollRef} className={only ? "pb-2" : "pb-48 pt-3"}>
        {activeTab === 'income' && (
          /* ===== INCOME TAB ===== */
          <div className="space-y-3" data-testid="income-tab-content">
            {only !== 'balances' && (<>
            {!only && (
              <p className={`text-[13px] font-bold uppercase tracking-[0.08em] ${sectionLabel}`}><span className="wy-star">✦ </span>Amount to budget</p>
            )}
            <div
              className="bg-white rounded-2xl p-4 cursor-pointer"
              style={cardShadow}
              onClick={() => {
                if (!editingBudgetAmount) {
                  setEditingBudgetAmount(true);
                  setLocalBudgetAmount(monthlyBudget > 0 ? monthlyBudget.toString() : '');
                }
              }}
              data-testid="amount-to-budget-card"
            >
              {editingBudgetAmount ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-2xl font-light">{currency.symbol}</span>
                  <Input
                    type="number"
                    value={localBudgetAmount}
                    onChange={(e) => setLocalBudgetAmount(e.target.value)}
                    onBlur={() => { updateBudgetAmount(localBudgetAmount); setEditingBudgetAmount(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { updateBudgetAmount(localBudgetAmount); setEditingBudgetAmount(false); } }}
                    placeholder="0"
                    className="h-14 pl-11 text-2xl font-bold bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    autoFocus
                    data-testid="amount-to-budget-input"
                  
                  />
                </div>
              ) : (
                <>
                  <p className="text-[34px] leading-none font-bold text-gray-800" style={serifFont}>
                    <span className="text-gray-300 text-xl mr-1" style={serifFont}>{currency.symbol}</span>
                    {formattedBudget}
                  </p>
                  <p className="text-[13px] text-gray-400 mt-2">Your total income for the month</p>
                </>
              )}
            </div>

            {/* Copying last month's income lives here, beside the figure it
                sets, and only while there is nothing to overwrite. */}
            {monthlyBudget <= 0 && prevBudgetAmount > 0 && (
              <button
                onClick={() => updateBudgetAmount(prevBudgetAmount)}
                className="w-full bg-white rounded-2xl p-3.5 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                style={cardShadow}
                data-testid="copy-prev-income-btn"
              >
                <span
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: plan ? '#F5F2FA' : isPink ? '#FBE4F1' : isWarm ? '#F0EAE0' : '#EEF1FF' }}
                >
                  <Copy className="w-[18px] h-[18px]" style={{ color: plan ? '#6C6879' : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE' }} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold text-gray-800">Same as {prevMonthName}</span>
                  <span className="block text-[12px] text-gray-500 mt-0.5">{formatCurrency(prevBudgetAmount)}</span>
                </span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#8A8194' }} />
              </button>
            )}
            </>)}

            {only !== 'amount' && (<>
            {/* Leftover from Last Month — the balance each card starts the month with */}
            {only === 'balances' ? (
              <p className="text-[12.5px] leading-[17px] font-medium text-gray-600">
                What each card carries over from {prevMonthName} as its starting balance. Take {prevMonthName}'s closing figures below, or type any row yourself.
              </p>
            ) : (
              <p className={`text-[13px] font-bold uppercase tracking-[0.08em] ${sectionLabel}`}><span className="wy-star">✦ </span>Leftover from last month</p>
            )}
            {hasCarrySuggestion && (
              <button
                onClick={applyCarrySuggestion}
                className="w-full bg-white rounded-2xl p-3.5 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                style={cardShadow}
                data-testid="carry-balances-btn"
              >
                <span
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: plan ? '#F5F2FA' : isPink ? '#FBE4F1' : isWarm ? '#F0EAE0' : '#EEF1FF' }}
                >
                  <Copy className="w-[18px] h-[18px]" style={{ color: plan ? '#6C6879' : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE' }} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold text-gray-800">Use {prevMonthName}'s closing balances</span>
                  <span className="block text-[12px] text-gray-500 mt-0.5">
                    {carryCardCount} card{carryCardCount === 1 ? '' : 's'} · {formatCurrency(carrySuggestionTotal)}
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#8A8194' }} />
              </button>
            )}

            <div className="bg-white rounded-2xl p-3.5" style={cardShadow}>
              <div className="space-y-0.5">
                {cards.filter(c => c.type !== 'credit').map(card => {
                  const savedAlloc = budgetSettings.leftover_allocations_by_month?.[viewMonthKey]?.[card.name] || 0;
                  const localVal = leftoverAllocations[card.name];
                  const displayVal = localVal !== undefined ? localVal : (savedAlloc || '');
                  // "auto" means: this is still exactly what was carried in, untouched.
                  const isCarried = localVal === undefined && !!carryFlagsForMonth[card.name];
                  return (
                    <div key={card.name} className="flex items-center gap-3 py-1" data-testid={`leftover-card-${card.name}`}>
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[12px] font-bold ${plan ? 'wy-plan-avatar' : (colorMap[card.color]?.bg || 'bg-gray-400')}`}>
                        {card.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-[14px] text-gray-800 flex-1 truncate flex items-center gap-2" style={{ fontWeight: 500 }}>
                        <span className="truncate">{card.name}</span>
                        {isCarried && (
                          <span
                            className="text-[9.5px] font-bold uppercase tracking-wide rounded-full px-2 py-[3px] flex-shrink-0"
                            style={plan ? { color: '#2F8756', background: '#DFF2E7', textTransform: 'none', letterSpacing: 0, fontSize: 11 } : { color: '#3F7A55', background: '#E8F3EC' }}
                          >{plan ? 'Carried' : 'carried'}</span>
                        )}
                      </span>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currency.symbol}</span>
                        <Input
                          type="number"
                          value={displayVal}
                          onChange={(e) => setLeftoverAllocations(prev => ({ ...prev, [card.name]: e.target.value }))}
                          onBlur={(e) => handleLeftoverBlur(card.name, e.target.value)}
                          placeholder="0"
                          className="w-[122px] h-9 pl-7 text-[14px] font-semibold rounded-full text-right pr-3"
                          style={{ background: leftoverInputBg }}
                          data-testid={`leftover-input-${card.name}`}
                        
                  />
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalLeftover > 0 && (
                <p className={`text-sm font-bold ${accentText} mt-3 text-right`} data-testid="leftover-total">
                  Total: {formatCurrency(totalLeftover)}
                </p>
              )}
            </div>

            {/* Summary Cards */}
            {(!only || only === 'income') && (
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-2xl p-3.5" style={{ background: leftoverTileBg }} data-testid="total-leftover-card">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: leftoverTileText }}>Total Leftover</p>
                <p className="text-xl font-bold" style={{ color: leftoverTileText, ...serifFont }}>{formatCurrency(totalLeftover)}</p>
                <p className="text-[10.5px] font-semibold mt-0.5" style={{ color: leftoverTileText, opacity: 0.75 }}>from last month</p>
              </div>
              <div className="rounded-2xl p-3.5" style={{ background: availableTileBg }} data-testid="available-card">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: availableTileText }}>Available</p>
                <p className="text-xl font-bold" style={{ color: availableTileText, ...serifFont }}>{formatCurrency(available)}</p>
                <p className="text-[10.5px] font-semibold mt-0.5" style={{ color: availableTileText, opacity: 0.75 }}>income + leftover</p>
              </div>
            </div>
            )}
            </>)}

          </div>
        )}

        {activeTab === 'budgets' && (
          /* ===== BUDGETS TAB ===== */
          <div className="space-y-3" data-testid="budgets-tab-content">
            {totalAllocated === 0 && copyableItems.length > 0 && (
              <button
                onClick={openCopySheet}
                className="w-full bg-white rounded-2xl p-3.5 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                style={cardShadow}
                data-testid="copy-last-month-row"
              >
                <span
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: plan ? '#F5F2FA' : isPink ? '#FBE4F1' : isWarm ? '#F0EAE0' : '#EEF1FF' }}
                >
                  <Copy className="w-[18px] h-[18px]" style={{ color: plan ? '#6C6879' : isPink ? '#e91e8c' : isWarm ? '#2D5A3D' : '#4361EE' }} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold text-gray-800">Start from {prevMonthName}</span>
                  <span className="block text-[12px] text-gray-500 mt-0.5">
                    {copyableItems.length} item{copyableItems.length === 1 ? '' : 's'} · {formatCurrency(copyableTotal)}
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#8A8194' }} />
              </button>
            )}
            {/* Total Allocated — donut + breakdown */}
            {(() => {
              // Theme-aware category colors — pulled from monthly chart palette in BudgetTab.jsx
              // Blue spending uses the main theme blue (#4361EE) instead of palette slot 0 (red) to avoid clashing
              const catColors = isPink
                ? { spending: '#e91e8c', savings: '#11B981', debts: '#3C82F6' }
                : isWarm
                  ? { spending: '#9B6B6B', savings: '#7C9070', debts: '#B07D50' }
                  : { spending: '#4361EE', savings: '#22c55e', debts: '#f97316' };

              // Shares of total available (clamped 0..100)
              const base = budgetBase > 0 ? budgetBase : 1;
              const spendingPct = Math.max(0, (totalCategoryBudgets / base) * 100);
              const savingsPct = Math.max(0, (savingsDeposits / base) * 100);
              const debtsPct = Math.max(0, (totalDebtBudgetsVal / base) * 100);
              const setPct = Math.min(100, spendingPct + savingsPct + debtsPct);

              const stop1 = Math.min(spendingPct, 100);
              const stop2 = Math.min(spendingPct + savingsPct, 100);
              const stop3 = Math.min(spendingPct + savingsPct + debtsPct, 100);
              const trackColor = isPink ? '#F3ECF7' : isWarm ? '#E8EDE4' : '#E0E7FF';

              const donutBg = `conic-gradient(${catColors.spending} 0% ${stop1}%, ${catColors.savings} ${stop1}% ${stop2}%, ${catColors.debts} ${stop2}% ${stop3}%, ${trackColor} ${stop3}% 100%)`;

              const isOverAllocated = remaining < 0;
              const allocatedBarPct = Math.min(100, Math.max(0, (totalAllocated / base) * 100));
              const allocatedBarColor = isOverAllocated ? '#EF4444' : (isPink ? '#e91e8c' : isWarm ? '#9B6B6B' : '#4361EE');

              return (
                <>
                  <div className="bg-white rounded-2xl p-4" style={cardShadow} data-testid="total-allocated-card">
                    <div className="flex items-center gap-3.5">
                      {/* Donut */}
                      <div className="relative flex-shrink-0" style={{ width: 88, height: 88 }}>
                        <div className="w-full h-full rounded-full" style={{ background: donutBg }} />
                        <div className="absolute inset-[10px] bg-white rounded-full flex flex-col items-center justify-center">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 leading-none">Set</span>
                          <span className="text-lg font-bold text-gray-800 leading-tight" style={serifFont}>{Math.round(setPct)}%</span>
                        </div>
                      </div>

                      {/* Breakdown legend */}
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {[
                          { label: 'Spending', color: catColors.spending, amount: totalCategoryBudgets },
                          { label: 'Savings', color: catColors.savings, amount: savingsDeposits },
                          { label: 'Debts', color: catColors.debts, amount: totalDebtBudgetsVal },
                        ].map((row) => (
                          <div key={row.label} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: row.color }} />
                              <span className="text-[14px] text-gray-800 truncate" style={{ fontWeight: 500 }}>{row.label}</span>
                            </div>
                            <span className="text-[13px] font-bold text-gray-800 flex-shrink-0">{formatCurrency(row.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Total Allocated — its own card, separate from the donut */}
                  <div className="bg-white rounded-2xl p-4" style={cardShadow} data-testid="total-allocated-bar-card">
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Total Allocated · {formatCurrency(totalAllocated)}</span>
                      <span className="text-[13px] font-bold" style={{ color: isOverAllocated ? '#EF4444' : '#22C55E' }}>
                        {isOverAllocated ? `${formatCurrency(Math.abs(remaining))} over` : `${formatCurrency(remaining)} left`}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: trackColor }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${allocatedBarPct}%`, background: allocatedBarColor }}
                      />
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Sub-tabs */}
            <div className={`${toggleBg} rounded-full p-1 flex gap-1`}>
              <button
                onClick={() => setBudgetViewType('spending')}
                className={`flex-1 py-2 text-[13px] font-bold rounded-full transition-all whitespace-nowrap ${
                  budgetViewType === 'spending' ? `bg-white shadow-sm ${activeTabText}` : 'text-gray-400'
                }`}
                data-testid="budget-spending-tab"
              >
                Spending
              </button>
              <button
                onClick={() => setBudgetViewType('savings')}
                className={`flex-1 py-2 text-[13px] font-bold rounded-full transition-all whitespace-nowrap ${
                  budgetViewType === 'savings'
                    ? `bg-white shadow-sm ${activeTabText}`
                    : 'text-gray-400'
                }`}
                data-testid="budget-savings-tab"
              >
                Savings/Invest.
              </button>
              <button
                onClick={() => setBudgetViewType('debts')}
                className={`flex-1 py-2 text-[13px] font-bold rounded-full transition-all whitespace-nowrap ${
                  budgetViewType === 'debts'
                    ? `bg-white shadow-sm ${activeTabText}`
                    : 'text-gray-400'
                }`}
                data-testid="budget-debts-tab"
              >
                Debts
              </button>
            </div>

            {/* Category Lists */}
            <div className="bg-white rounded-2xl overflow-hidden" style={cardShadow}>
              {/* Spending */}
              {budgetViewType === 'spending' && (
                <div className="divide-y divide-gray-50">
                  {categoriesWithActivity.map(({ name: cat, budget }) => {
                    return (
                      <div key={cat} className="flex items-center gap-3 py-3.5 px-4" data-testid={`budget-category-${cat}`}>
                        <span className="text-[14px] text-gray-800 truncate flex-1 min-w-0" style={{ fontWeight: 500 }}>{cat}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <>
                              <span className={`text-[14px] font-semibold min-w-[74px] text-right ${budget > 0 ? 'text-gray-800' : 'text-gray-300 italic'}`}>
                                {budget > 0 ? formatCurrency(budget) : 'Not set'}
                              </span>
                              <button
                                onClick={() => setBudgetPopup({
                                  label: cat,
                                  value: monthlyCategoryBudgets[cat]?.toString() || '',
                                  accent: isPink ? '#e879a0' : isWarm ? '#5D6B4D' : '#6366f1',
                                  onSave: (val) => {
                                    const v = parseFloat(val) || 0;
                                    const mb = { ...monthlyCategoryBudgets, [cat]: v };
                                    saveUserData({ category_budgets_by_month: { ...categoryBudgets, [viewMonthKey]: mb } });
                                  }
                                })}
                                className="w-9 h-9 flex items-center justify-center text-base bg-gray-50 rounded-full hover:bg-gray-100 transition-colors"
                                data-testid={`edit-budget-${cat}`}
                              >
                                ✏️
                              </button>
                            </>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Savings/Investments */}
              {budgetViewType === 'savings' && (availableSinkingFunds.length > 0 || availableInvestmentAccounts.length > 0) && (
                <div className="divide-y divide-gray-50">
                  {availableSinkingFunds.map(fund => {
                    const savBudget = monthlySavingsBudgets[fund.id] || 0;
                    return (
                      <div key={fund.id} className="flex items-center gap-3 py-3.5 px-4" data-testid={`budget-fund-${fund.id}`}>
                        <span className="text-[14px] font-semibold text-gray-700 truncate flex-1 min-w-0">{fund.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <>
                              <span className={`text-[14px] font-semibold min-w-[74px] text-right ${savBudget !== 0 ? (savBudget < 0 ? 'text-amber-500' : 'text-gray-800') : 'text-gray-300 italic'}`}>
                                {savBudget !== 0 ? formatCurrency(savBudget) : 'Not set'}
                              </span>
                              <button
                                onClick={() => setBudgetPopup({
                                  label: fund.name,
                                  hint: 'Use negative for planned withdrawal e.g. -100',
                                  value: savBudget !== 0 ? savBudget.toString() : '',
                                  accent: isWarm ? '#5D6B4D' : '#8b5cf6',
                                  allowNegative: true,
                                  onSave: (val) => {
                                    saveSavingsBudget(fund.id, parseFloat(val) || 0);
                                  }
                                })}
                                className={`w-9 h-9 flex items-center justify-center text-base rounded-full transition-colors ${isWarm ? 'bg-[#E8EDE4] hover:bg-[#D8E0D4]' : 'bg-purple-50 hover:bg-purple-100'}`}
                              >
                                ✏️
                              </button>
                            </>
                        </div>
                      </div>
                    );
                  })}
                  {availableInvestmentAccounts.map(inv => {
                    const invBudget = monthlySavingsBudgets[inv.id] || 0;
                    return (
                      <div key={inv.id} className="flex items-center gap-3 py-3.5 px-4" data-testid={`budget-inv-${inv.id}`}>
                        <span className="text-[14px] font-semibold text-gray-700 truncate flex-1 min-w-0">{inv.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <>
                              <span className={`text-[14px] font-semibold min-w-[74px] text-right ${invBudget !== 0 ? (invBudget < 0 ? 'text-amber-500' : 'text-gray-800') : 'text-gray-300 italic'}`}>
                                {invBudget !== 0 ? formatCurrency(invBudget) : 'Not set'}
                              </span>
                              <button
                                onClick={() => setBudgetPopup({
                                  label: inv.name,
                                  hint: 'Use negative for planned withdrawal e.g. -100',
                                  value: invBudget !== 0 ? invBudget.toString() : '',
                                  accent: isWarm ? '#4A6B6B' : '#3b82f6',
                                  allowNegative: true,
                                  onSave: (val) => {
                                    saveSavingsBudget(inv.id, parseFloat(val) || 0);
                                  }
                                })}
                                className={`w-9 h-9 flex items-center justify-center text-base rounded-full transition-colors ${isWarm ? 'bg-[#E4EDED] hover:bg-[#D4DDDD]' : 'bg-blue-50 hover:bg-blue-100'}`}
                              >
                                ✏️
                              </button>
                            </>
                        </div>
                      </div>
                    );
                  })}

                </div>
              )}

              {/* Debts */}
              {budgetViewType === 'debts' && (userDebts || []).length > 0 && (
                <div className="divide-y divide-gray-50">
                  {(userDebts || []).map(debt => {
                    const dBudget = monthlyDebtBudgets[debt.id] || 0;
                    return (
                      <div key={debt.id} className="flex items-center gap-3 py-3.5 px-4" data-testid={`budget-debt-${debt.id}`}>
                        <span className="text-[14px] font-semibold text-gray-700 truncate flex-1 min-w-0">{debt.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <>
                              <span className={`text-[14px] font-semibold min-w-[74px] text-right ${dBudget > 0 ? 'text-gray-800' : 'text-gray-300 italic'}`}>
                                {dBudget > 0 ? formatCurrency(dBudget) : 'Not set'}
                              </span>
                              <button
                                onClick={() => setBudgetPopup({
                                  label: debt.name,
                                  value: dBudget > 0 ? dBudget.toString() : '',
                                  accent: isWarm ? '#8B6B4D' : '#f97316',
                                  onSave: (val) => {
                                    saveDebtBudget(debt.id, parseFloat(val) || 0);
                                  }
                                })}
                                className={`w-9 h-9 flex items-center justify-center text-base rounded-full transition-colors ${isWarm ? 'bg-[#F0E8E0] hover:bg-[#E8DDD0]' : 'bg-orange-50 hover:bg-orange-100'}`}
                              >
                                ✏️
                              </button>
                            </>
                        </div>
                      </div>
                    );
                  })}

                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          /* ===== NOTES TAB ===== */
          <div className="space-y-3" data-testid="notes-tab-content">
            <div className={"bg-white rounded-2xl p-2 shadow-sm" + (plan ? " wy-plan-memos" : "")} style={cardShadow}>
              {plan && (
                <div className="flex items-baseline justify-between" style={{ padding: '8px 10px 6px' }}>
                  <span style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' }}>{monthName} memos</span>
                  {reminderLines.length > 0 && <span style={{ fontSize: 13, fontWeight: 600, color: '#8B879A' }}>{reminderLines.length}</span>}
                </div>
              )}
              {reminderLines.length === 0 && !addingReminder && (
                <p className="text-[13px] text-gray-300 px-3 py-3.5">No memos yet for {monthName}.</p>
              )}
              {reminderLines.map((line, i) => (
                <div key={i} className="flex items-center gap-2.5 px-2.5 py-2.5 border-b border-gray-50 last:border-b-0">
                  <div className={plan ? "w-3 h-7 flex items-center justify-center flex-shrink-0" : "w-7 h-7 rounded-lg flex items-center justify-center text-[13px] flex-shrink-0"} style={{ background: reminderChipBg }}>
                    {plan ? <span style={{ display: 'block', width: 8, height: 8, borderRadius: 999, background: '#B9A6F0' }} /> : reminderIcon}
                  </div>
                  {editingReminderIndex === i ? (
                    <textarea
                      autoFocus
                      rows={1}
                      ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      value={reminderDraft}
                      onChange={(e) => setReminderDraft(e.target.value.replace(/\n/g, ' '))}
                      onBlur={() => saveEditReminder(i)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                      className="flex-1 text-[14px] text-gray-800 bg-transparent border-0 focus:outline-none focus:ring-0 resize-none leading-snug p-0" style={{ fontWeight: 500, overflow: 'hidden' }}
                      data-testid={`reminder-input-${i}`}
                    />
                  ) : (
                    <button
                      onClick={() => startEditReminder(i)}
                      className="flex-1 text-left text-[14px] text-gray-800" style={{ fontWeight: 500 }}
                      data-testid={`reminder-row-${i}`}
                    >
                      {line}
                    </button>
                  )}
                  <button
                    onClick={() => deleteReminder(i)}
                    className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 flex-shrink-0"
                    aria-label="Delete reminder"
                    data-testid={`delete-reminder-${i}`}
                  >
                    {plan ? <X className="w-3.5 h-3.5" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
              ))}

              {addingReminder ? (
                <div className="flex items-center gap-2.5 px-2.5 py-2.5">
                  <div className={plan ? "w-3 h-7 flex items-center justify-center flex-shrink-0" : "w-7 h-7 rounded-lg flex items-center justify-center text-[13px] flex-shrink-0"} style={{ background: reminderChipBg }}>
                    {plan ? <span style={{ display: 'block', width: 8, height: 8, borderRadius: 999, background: '#B9A6F0' }} /> : reminderIcon}
                  </div>
                  <textarea
                    autoFocus
                    rows={1}
                    ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    value={newReminderDraft}
                    onChange={(e) => setNewReminderDraft(e.target.value.replace(/\n/g, ' '))}
                    onBlur={saveNewReminder}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                    placeholder={`A memo for ${monthName}...`}
                    className="flex-1 text-[14px] text-gray-800 placeholder-gray-300 bg-transparent border-0 focus:outline-none focus:ring-0 resize-none leading-snug p-0" style={{ fontWeight: 500, overflow: 'hidden' }}
                    data-testid="new-reminder-input"
                  />
                </div>
              ) : (
                <button
                  onClick={() => setAddingReminder(true)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2.5 text-left"
                  data-testid="add-reminder-btn"
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: addReminderChipBg }}>
                    <span className="text-[14px] font-bold" style={{ color: sectionLabelColor }}>+</span>
                  </div>
                  <span className="text-[14px] font-medium text-gray-300">Add a memo…</span>
                </button>
              )}
            </div>

            {reminderLines.length === 0 && prevMonthNotes.trim() && (
              <button
                onClick={copyPreviousMonthNotes}
                className="text-[12.5px] font-bold px-3.5 py-2 rounded-full bg-white"
                style={{ color: sectionLabelColor, ...cardShadow }}
                data-testid="copy-prev-month-notes-btn"
              >
                Copy last month's reminders
              </button>
            )}
          </div>
        )}
      </div>

      {/* Floating Done Button — stays reachable while the setup content scrolls with the page */}
      {!only && (
        <div
          className="fixed left-0 right-0 z-30 flex justify-center pb-28 pt-10 pointer-events-none"
          style={{
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.72) 45%, rgba(255,255,255,0.92) 100%)',
          }}
        >
          <button
            onClick={onClose}
            className={`pointer-events-auto px-10 py-3 rounded-full text-white font-semibold text-[15px] shadow-md active:scale-[0.98] transition-transform`}
            style={{ background: plan ? '#1F1E24' : isPink ? '#BA5580' : isWarm ? '#607F5E' : '#5877A6' }}
            data-testid="save-budget-btn"
          >
            Done
          </button>
        </div>
      )}

      {showCopySheet && (
        <CopyLastMonthSheet
          onBack={onBack}
          groups={copyGroups}
          prevMonthName={prevMonthName}
          monthName={monthName}
          selected={copySelected}
          onToggle={(k) => setCopySelected(prev => ({ ...prev, [k]: !prev[k] }))}
          onSelectAll={() => {
            const next = {};
            copyableItems.forEach(i => { next[i.key] = true; });
            setCopySelected(next);
          }}
          onClearAll={() => setCopySelected({})}
          copyNotes={copyNotes}
          setCopyNotes={setCopyNotes}
          newNotesCount={newNoteLines.length}
          onConfirm={runCopyFromLastMonth}
          onCancel={() => { setShowCopySheet(false); if (only === 'copy') onClose?.(); }}
          isPink={isPink}
          isWarm={isWarm}
          theme={theme}
        />
      )}

      {/* Budget Edit Popup - floats above keyboard */}
      {budgetPopup && (
        <BudgetEditPopup
          label={budgetPopup.label}
          hint={budgetPopup.hint}
          initialValue={budgetPopup.value}
          accent={budgetPopup.accent}
          currency={currency}
          allowNegative={budgetPopup.allowNegative}
          isWarm={isWarm}
          isPink={isPink}
          theme={theme}
          onSave={(val) => { budgetPopup.onSave(val); setBudgetPopup(null); }}
          onCancel={() => setBudgetPopup(null)}
        />
      )}

      {/* Reset confirmation overlay — z-[60] to sit above this z-50 sheet */}
      {showResetConfirm && (() => {
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const monthName = viewMonth ? monthNames[viewMonth.getMonth()] : '';
        const year = viewMonth ? viewMonth.getFullYear() : '';
        return (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowResetConfirm(false)}>
            <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#FDE7EC' }}>
                  <Trash2 className="w-6 h-6" style={{ color: '#E11D48' }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Reset {monthName}?</h3>
              <p className="text-sm text-gray-500 text-center mb-6">This will clear all your budget setup and transactions for {monthName} {year}. This action cannot be undone.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="flex-1 py-3 rounded-full border border-gray-200 bg-white text-sm font-bold text-gray-500"
                >Cancel</button>
                <button
                  onClick={() => {
                    onResetMonth?.();
                    setShowResetConfirm(false);
                    onClose?.();
                  }}
                  className="flex-1 py-3 rounded-full text-sm font-bold text-white active:scale-[0.98] transition-transform"
                  style={{ background: '#E11D48' }}
                >Yes, Reset</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MonthlyBudgetSetup;
